/**
 * PnlBadgePrimitive — draws a single, eye-catching LIVE P&L badge pinned to the
 * TOP-LEFT of the price pane (fixed pixel position, independent of price/time), so
 * the running option basket's unrealised P&L is impossible to miss and never sits
 * under the price axis or the option-profile rail on the right.
 *
 * Used by the payoff-zones overlay: green ▲ for profit, red ▼ for loss.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface PnlBadge {
  /** Live P&L in INR (signed). */
  value: number;
  /** Optional sub-label, e.g. "#12 · Iron Condor". */
  sub?: string;
  /** Extra info lines under the sub-label (e.g. "SL −₹1,200 · TP +₹800", "Entry 61,014"). */
  lines?: string[];
  /** Amber warning line (e.g. "⚠ Adjustment needed"). */
  warn?: string;
  /** Drop the leading amount row. For badges that carry an EXPLANATION rather
   *  than a P&L — a strategy muted on this chart has no P&L, and printing
   *  "+₹0" above the reason reads as a flat trade instead of no trade.
   *  `value` is still required (it picks the accent colour) and should be 0. */
  noAmount?: boolean;
}

/** The badge's drawn box in CSS px (relative to the pane top-left) — used for click hit-testing. */
export interface BadgeRect { x: number; y: number; w: number; h: number }

class PnlBadgeRenderer implements IPrimitivePaneRenderer {
  constructor(private readonly badge: PnlBadge | null, private readonly onRect?: (r: BadgeRect | null) => void) {}

  draw(target: CanvasRenderingTarget2D): void {
    const b = this.badge;
    if (!b || !Number.isFinite(b.value)) { this.onRect?.(null); return; }
    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const up = b.value >= 0;
      const fg = up ? '#1b8a7e' : '#c62828';
      const bg = up ? 'rgba(38,166,154,0.16)' : 'rgba(239,83,80,0.16)';
      const arrow = up ? '▲' : '▼';
      const amount = `${up ? '+' : '−'}₹${Math.abs(Math.round(b.value)).toLocaleString('en-IN')}`;
      const text = `${arrow} ${amount}`;

      const padX = 8 * hpr, padY = 5 * vpr;
      const x0 = 6 * hpr; // y0 is set to the BOTTOM-left after the pill height is known
      const mainPx = Math.round(15 * Math.min(hpr, vpr));
      const subPx = Math.round(9.5 * Math.min(hpr, vpr));
      const gap = 3 * vpr;
      const mainFont = `800 ${mainPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const subFont = `600 ${subPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const subColor = up ? 'rgba(27,138,126,0.9)' : 'rgba(198,40,40,0.9)';
      const warnColor = '#e08600';

      // Rows: main P&L, sub, extra info lines, then the warning (bold amber).
      type Row = { text: string; font: string; color: string; px: number };
      const rows: Row[] = b.noAmount ? [] : [{ text, font: mainFont, color: fg, px: mainPx }];
      if (b.sub) rows.push({ text: b.sub, font: subFont, color: subColor, px: subPx });
      for (const ln of (b.lines || [])) rows.push({ text: ln, font: subFont, color: subColor, px: subPx });
      if (b.warn) rows.push({ text: b.warn, font: `800 ${subPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`, color: warnColor, px: subPx });

      ctx.save();
      // Measure widest row for the pill width.
      let contentW = 0;
      for (const rw of rows) { ctx.font = rw.font; contentW = Math.max(contentW, ctx.measureText(rw.text).width); }
      const lineH = rows.reduce((s, rw, i) => s + rw.px + (i > 0 ? gap : 0), 0);
      const w = contentW + padX * 2;
      const h = lineH + padY * 2;
      // BOTTOM-left of the pane (the top-left is occupied by the OHLC legend).
      // Cleared above the charting library's attribution mark, which also sits
      // bottom-left: at a 6px offset it covered the first characters of the LAST
      // row of every multi-row badge — the SL/TP line on a live basket, and the
      // whole explanation on a strategy notice.
      const y0 = bitmapSize.height - h - 26 * vpr;
      // Publish the drawn box in CSS px (pane-relative) for click hit-testing.
      this.onRect?.({ x: x0 / hpr, y: y0 / vpr, w: w / hpr, h: h / vpr });

      // Rounded pill background + accent border.
      const r = 6 * Math.min(hpr, vpr);
      ctx.beginPath();
      ctx.moveTo(x0 + r, y0);
      ctx.arcTo(x0 + w, y0, x0 + w, y0 + h, r);
      ctx.arcTo(x0 + w, y0 + h, x0, y0 + h, r);
      ctx.arcTo(x0, y0 + h, x0, y0, r);
      ctx.arcTo(x0, y0, x0 + w, y0, r);
      ctx.closePath();
      ctx.fillStyle = bg;
      ctx.fill();
      ctx.lineWidth = Math.max(1, Math.min(hpr, vpr));
      ctx.strokeStyle = b.warn ? warnColor : fg;
      ctx.stroke();

      // Draw each row top-down.
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      let y = y0 + padY;
      for (let i = 0; i < rows.length; i++) {
        const rw = rows[i];
        if (i > 0) y += gap;
        ctx.font = rw.font;
        ctx.fillStyle = rw.color;
        ctx.fillText(rw.text, x0 + padX, y);
        y += rw.px;
      }
      ctx.restore();
    });
  }
}

class PnlBadgePaneView implements IPrimitivePaneView {
  constructor(private readonly parent: PnlBadgePrimitive) {}
  zOrder() { return 'top' as const; } // on top of everything — it's the headline
  renderer(): IPrimitivePaneRenderer | null {
    return new PnlBadgeRenderer(this.parent.badge, (r) => { this.parent.lastRect = r; });
  }
}

export class PnlBadgePrimitive implements ISeriesPrimitive<Time> {
  badge: PnlBadge | null = null;
  /** Last drawn box in CSS px (pane-relative) — for click hit-testing. */
  lastRect: BadgeRect | null = null;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: PnlBadgePaneView[];

  constructor(badge: PnlBadge | null) {
    this.badge = badge;
    this._paneViews = [new PnlBadgePaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }

  updateAllViews(): void { /* renderer reads this.badge fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(badge: PnlBadge | null): void {
    this.badge = badge;
    this.chart?.applyOptions({});
  }
}
